import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { Edit as EditIcon, Delete as DeleteIcon, Plus, Tag as HrAbsenceTypeLogoIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const CODICE_OPTIONS = [
  { value: 'ferie', label: 'Ferie' },
  { value: 'permesso_retribuito', label: 'Permesso Retribuito' },
  { value: 'permesso_orario', label: 'Permesso Orario' },
  { value: 'permesso_non_retribuito', label: 'Permesso Non Retribuito' },
  { value: 'malattia', label: 'Malattia' },
  { value: 'rol_ex_festivita', label: 'ROL / Ex Festivita' },
  { value: 'altra_assenza', label: 'Altra Assenza' }
];

function boolLabel(value) {
  return value ? 'Si' : 'No';
}

export default function HrTipiAssenza() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [nome, setNome] = useState('');
  const [codice, setCodice] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [colore, setColore] = useState('#2563eb');
  const [retribuito, setRetribuito] = useState(true);
  const [scalaDaSaldo, setScalaDaSaldo] = useState(true);
  const [attivo, setAttivo] = useState(true);

  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('nome');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await api.get('/tipi-assenza');
      setItems(res.data || []);
    } catch (error) {
      console.error('Error fetching tipi_assenza:', error);
      toast.error('Errore nel caricamento dei tipi assenza');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const sortedItems = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];
      if (valueA === null || valueA === undefined) return 1;
      if (valueB === null || valueB === undefined) return -1;
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return order === 'asc' ? valueA - valueB : valueB - valueA;
      }
      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      if (strA < strB) return order === 'asc' ? -1 : 1;
      if (strA > strB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [items, order, orderBy]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    setNome('');
    setCodice('');
    setDescrizione('');
    setColore('#2563eb');
    setRetribuito(true);
    setScalaDaSaldo(true);
    setAttivo(true);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setNome(item.nome || '');
    setCodice(item.codice || '');
    setDescrizione(item.descrizione || '');
    setColore(item.colore || '#2563eb');
    setRetribuito(Boolean(item.retribuito));
    setScalaDaSaldo(Boolean(item.scala_da_saldo));
    setAttivo(Boolean(item.attivo));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!nome.trim()) {
      toast.error('Il nome è obbligatorio');
      return;
    }
    if (!codice) {
      toast.error('Il codice è obbligatorio');
      return;
    }

    const payload = {
      nome: nome.trim(),
      codice,
      descrizione: descrizione ? descrizione.trim() : null,
      colore: colore || null,
      retribuito,
      scala_da_saldo: scalaDaSaldo,
      attivo
    };

    try {
      setSaving(true);
      if (editingItem) {
        await api.put(`/tipi-assenza/${editingItem.id}`, payload);
        toast.success('Tipo assenza aggiornato');
      } else {
        await api.post('/tipi-assenza', payload);
        toast.success('Tipo assenza creato');
      }
      setDialogOpen(false);
      await fetchItems();
    } catch (error) {
      console.error('Error saving tipo_assenza:', error);
      toast.error(error.response?.data?.message || 'Errore nel salvataggio del tipo assenza');
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (item) => {
    setDeleteTarget(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/tipi-assenza/${deleteTarget.id}`);
      toast.success('Tipo assenza eliminato');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchItems();
    } catch (error) {
      console.error('Error deleting tipo_assenza:', error);
      toast.error(error.response?.data?.message || 'Errore nell\'eliminazione del tipo assenza');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <HrAbsenceTypeLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Tipi assenza
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Anagrafica centrale per ferie, permessi, malattia e altre assenze
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" color="primary" startIcon={<Plus size={18} />} onClick={openCreateDialog}>
          Nuovo tipo assenza
        </Button>
      </Box>

      <StyledCard>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === 'nome' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'nome'}
                    direction={orderBy === 'nome' ? order : 'asc'}
                    onClick={() => handleRequestSort('nome')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'codice' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'codice'}
                    direction={orderBy === 'codice' ? order : 'asc'}
                    onClick={() => handleRequestSort('codice')}
                  >
                    Codice
                  </TableSortLabel>
                </TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell align="center">Retribuito</TableCell>
                <TableCell align="center">Scala da saldo</TableCell>
                <TableCell align="center">Colore</TableCell>
                <TableCell align="center">Attivo</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : sortedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun tipo assenza presente
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedItems
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{item.nome}</TableCell>
                      <TableCell>
                        <Chip size="small" label={item.codice} variant="outlined" />
                      </TableCell>
                      <TableCell>{item.descrizione || '-'}</TableCell>
                      <TableCell align="center">{boolLabel(Boolean(item.retribuito))}</TableCell>
                      <TableCell align="center">{boolLabel(Boolean(item.scala_da_saldo))}</TableCell>
                      <TableCell align="center">
                        {item.colore ? (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                bgcolor: item.colore,
                                border: '1px solid rgba(0,0,0,0.12)'
                              }}
                            />
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Switch checked={Boolean(item.attivo)} disabled />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                          <Tooltip title="Modifica tipo assenza">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEditDialog(item)}
                              sx={{ mr: 1 }}
                            >
                              <EditIcon size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Elimina tipo assenza">
                            <IconButton size="small" color="error" onClick={() => askDelete(item)}>
                              <DeleteIcon size={18} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={sortedItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog open={dialogOpen} onClose={() => (saving ? null : setDialogOpen(false))} fullWidth maxWidth="sm">
        <DialogTitle>{editingItem ? 'Modifica tipo assenza' : 'Nuovo tipo assenza'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} fullWidth required />
            <FormControl fullWidth required>
              <InputLabel id="codice-tipo-assenza-label">Codice</InputLabel>
              <Select
                labelId="codice-tipo-assenza-label"
                label="Codice"
                value={codice}
                onChange={(e) => setCodice(e.target.value)}
              >
                {CODICE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Descrizione"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TextField
                label="Colore"
                type="color"
                value={colore || '#2563eb'}
                onChange={(e) => setColore(e.target.value)}
                sx={{ width: 110 }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'aria-label': 'Selettore colore' }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2 }}>
              <FormControlLabel
                control={<Switch checked={retribuito} onChange={(e) => setRetribuito(e.target.checked)} />}
                label="Retribuito"
              />
              <FormControlLabel
                control={<Switch checked={scalaDaSaldo} onChange={(e) => setScalaDaSaldo(e.target.checked)} />}
                label="Scala da saldo"
              />
              <FormControlLabel
                control={<Switch checked={attivo} onChange={(e) => setAttivo(e.target.checked)} />}
                label="Attivo"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Annulla
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1 }}>Conferma eliminazione</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Confermi l&apos;eliminazione del tipo assenza{' '}
            <strong>{deleteTarget?.nome || '-'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
            }}
            disabled={deleteLoading}
          >
            Annulla
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
